'use client';
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { usePathname } from 'next/navigation';
import { useConvex } from 'convex/react';
import { api } from '@/convex/_generated/api';
import type { Id } from '@/convex/_generated/dataModel';
import { useAuth } from '@/lib/auth';
import {
  createComposerUploadLifecycle,
  type ComposerUploadLifecycle,
} from '@/components/org-chat/composer-upload-lifecycle';
import { createComposerUploadServices } from '@/components/org-chat/composer-upload-service';
import { composerUploadProgress } from '@/lib/uploads/composer-upload-progress';
import { releaseComposerPreview } from '@/lib/uploads/sent-upload-previews';
import {
  createBackgroundUploadManager,
  type BackgroundUploadJob,
  type BackgroundUploadManager,
} from '@/lib/uploads/upload-manager/background-upload-manager';
import { browserUploadLocks } from '@/lib/uploads/upload-manager/background-upload-locks';
import {
  backgroundUploadActor,
  jobsInWorkspace,
} from '@/lib/uploads/upload-manager/background-upload-records';
import { browserPersistedUploadStore } from '@/lib/uploads/upload-manager/persisted-upload-store';
import { uploadSessionJournal } from '@/lib/uploads/upload-manager/upload-session-journal';
import { useBackgroundUploadReloadSafety } from '@/hooks/use-background-upload-reload-safety';

type Client = ReturnType<typeof useConvex>;
const sendId = (id: string) => id as Id<'orgThreadPendingUploadSends'>;

/** The manager's view of the server half (#4851). */
function backgroundUploadServer(client: Client) {
  const sends = api.organizations.threads.pending_upload_sends;
  return {
    progress: (
      pendingSendId: string,
      updates: Array<{ uploadId: string; uploadedBytes: number }>
    ) =>
      client.mutation(sends.reportPendingUploadProgress, {
        pendingSendId: sendId(pendingSendId),
        updates,
      }),
    complete: (args: {
      pendingSendId: string;
      uploadId: string;
      storageKey: string;
      workspaceFileId: string;
    }) =>
      client.mutation(sends.completePendingUpload, {
        ...args,
        pendingSendId: sendId(args.pendingSendId),
        workspaceFileId: args.workspaceFileId as Id<'workspaceFiles'>,
      }),
    fail: (args: {
      pendingSendId: string;
      uploadId: string;
      reason: 'too_large' | 'network' | 'server' | 'file_unavailable';
    }) =>
      client.mutation(sends.failPendingUpload, {
        ...args,
        pendingSendId: sendId(args.pendingSendId),
      }),
    remove: (args: { pendingSendId: string; uploadId: string }) =>
      client.mutation(sends.removePendingUpload, {
        ...args,
        pendingSendId: sendId(args.pendingSendId),
      }),
    pendingFiles: async (orgThreadId: string) => {
      const pending = await client.query(sends.getPendingUploadSendForThread, {
        orgThreadId: orgThreadId as Id<'orgThreads'>,
      });
      // Only the member who sent it holds its bytes; anyone else's copy is stale.
      return pending?.isOwnSend && pending.status === 'pending_uploads'
        ? { pendingSendId: pending.pendingSendId, files: pending.files }
        : null;
    },
  };
}

/** Runs resumed uploads exactly as the composer would, outside any page. */
function appUploadLifecycle(client: Client, orgSlug: string) {
  const lifecycle = createComposerUploadLifecycle({
    ...createComposerUploadServices({
      orgSlug,
      generatePrivateUploadUrlForMedia: (args) =>
        client.mutation(
          api.media.uploads.generatePrivateUploadUrlForMedia,
          args
        ),
      recordComposerUploads: async (uploads, referenceCategory) => {
        const records = await client.mutation(
          api.generations.mutations.upsertComposerUploadedFilesForCurrentUser,
          {
            orgSlug,
            uploads: uploads.map((upload) => ({
              storageKey: upload.storageKey,
              url: upload.url,
              filename: upload.filename,
              mimeType: upload.mimeType,
              sizeBytes: upload.sizeBytes,
              uploadId: upload.uploadId,
              ...(referenceCategory ? { referenceCategory } : {}),
            })),
          }
        );
        return new Map(records.map((record) => [record.r2Key, record]));
      },
      composerPutUploadCalls: {
        start: (args) =>
          client.action(api.media.uploads.startComposerPutUpload, args),
        complete: (args) =>
          client.action(api.media.uploads.completeComposerPutUpload, args),
        abort: (args) => client.action(api.media.browser_uploads.abort, args),
      },
      journal: uploadSessionJournal,
      reissueBrowserUploadSessionCapability: ({ sessionId }) =>
        client.mutation(
          api.media.uploads.reissueBrowserUploadSessionCapability,
          { sessionId: sessionId as Id<'browserUploadSessions'> }
        ),
    }),
    changed: () => undefined,
    progress: composerUploadProgress,
    releasePreview: releaseComposerPreview,
  });
  return lifecycle;
}

const Context = createContext<BackgroundUploadManager | null>(null);

/** The workspace a path is in: its first segment. */
const workspaceSlugOf = (pathname: string | null) =>
  pathname?.split('/')[1] ?? '';

/**
 * App-level owner of uploads a send took with it (#4844). Mounted above
 * every page, so route changes never unmount it; on load it resumes this
 * workspace's files from browser storage.
 */
export function BackgroundUploadsProvider({
  children,
}: {
  children: ReactNode;
}) {
  const client = useConvex();
  const actorId = backgroundUploadActor(useAuth());
  const orgSlug = workspaceSlugOf(usePathname());
  const [{ manager, lifecycles }] = useState(() => {
    const lifecycles = new Map<string, ComposerUploadLifecycle>();
    const manager = createBackgroundUploadManager({
      server: backgroundUploadServer(client),
      // This browser's IndexedDB + OPFS, so a reload can resume the files.
      store: browserPersistedUploadStore(),
      journal: uploadSessionJournal,
      progress: composerUploadProgress,
      // One tab per upload: another tab's live upload is never resumed here.
      locks: browserUploadLocks(),
      lifecycleFor: (slug) => {
        const existing = lifecycles.get(slug);
        if (existing) return existing;
        const created = appUploadLifecycle(client, slug);
        lifecycles.set(slug, created);
        return created;
      },
      report: (step, error) =>
        console.warn('[background-uploads] step failed', { step, error }),
    });
    return { manager, lifecycles };
  });
  useBackgroundUploadReloadSafety(manager);
  // Every member change stops the old member's jobs before anything of the
  // new one resumes (this effect runs before the one below).
  useEffect(() => {
    if (actorId === undefined || !manager.setActor(actorId)) return;
    for (const lifecycle of lifecycles.values()) lifecycle.dispose();
    lifecycles.clear();
  }, [manager, lifecycles, actorId]);
  useEffect(() => {
    if (!actorId || !orgSlug) return;
    void manager.resume(orgSlug);
  }, [manager, actorId, orgSlug]);
  // Probe browser storage early, so the first send can already go now.
  useEffect(() => {
    void manager.probeDurableStorage();
  }, [manager]);
  useEffect(() => {
    const flush = setInterval(() => void manager.flushProgress(), 3_000);
    const sweep = setInterval(() => manager.sweepStalls(), 5_000);
    return () => {
      clearInterval(flush);
      clearInterval(sweep);
    };
  }, [manager]);
  return <Context.Provider value={manager}>{children}</Context.Provider>;
}

export function useBackgroundUploadManager() {
  return useContext(Context);
}

const NO_JOBS: BackgroundUploadJob[] = [];
/**
 * Every file still uploading (or failed) for a waiting send in the workspace
 * on screen, live. Another workspace's uploads keep going, unseen here.
 */
export function useBackgroundUploadJobs(): BackgroundUploadJob[] {
  const manager = useContext(Context);
  const orgSlug = workspaceSlugOf(usePathname());
  const jobs = useSyncExternalStore(
    manager?.subscribe ?? (() => () => undefined),
    manager?.jobs ?? (() => NO_JOBS),
    () => NO_JOBS
  );
  return useMemo(() => jobsInWorkspace(jobs, orgSlug), [jobs, orgSlug]);
}
